import { Box, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";

import { WEEKLY_WORKER_PAYMENT_MODULE } from "../../constants";
import { workerValidationRules } from "../../pages/worker/validation";
import { HumanResourceService } from "../../services";
import { useHasPermission } from "../../utils";
import { ActionButton, FormDialog, InputField, PhoneInput } from "../v3";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditWorker = ({ open, onClose, workerId, onSuccess }) => {
  // Decision: the Welfare Amount input is intentionally gated on the module code only
  // (WEEKLY_WORKER_PAYMENT_MODULE), not on a feature permission like VIEW_WORKER_WELFARE (which gates
  // welfare display elsewhere). Anyone who can edit a worker and has the module may set it.
  const hasWeeklyWorkerPaymentModule = useHasPermission(WEEKLY_WORKER_PAYMENT_MODULE);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [workerDetails, setWorkerDetails] = useState({
    name: undefined,
    primaryPhone: undefined,
    designation: undefined,
    salary: undefined,
    overtimeHourlyRate: undefined,
    oldBalance: undefined,
    aadhaarNumber: undefined,
    isGroupWorker: false,
    welfareAmount: undefined,
  });

  const isValid = isPhoneValid(workerDetails.primaryPhone);
  const showError = touched && !isValid;

  useEffect(() => {
    if (open && workerId) {
      fetchData(workerId);
    } else if (open && !workerId) {
      handleClearForm();
    }
  }, [open, workerId]);

  const fetchData = async (id) => {
    try {
      const response = await HumanResourceService.getEmployeeById(id);
      setWorkerDetails({
        name: response?.data?.name,
        primaryPhone: response?.data?.primaryPhone || undefined,
        designation: response?.data?.designation || undefined,
        salary: Number(response?.data?.salary) || undefined,
        overtimeHourlyRate: Number(response?.data?.overtimeHourlyRate) || undefined,
        aadhaarNumber: response?.data?.aadhaarNumber || undefined,
        isGroupWorker: Boolean(response?.data?.isGroupWorker),
        welfareAmount: Number(response?.data?.welfareAmount) || undefined,
      });
    } catch (error) {
      toast.error("Failed to fetch worker details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleClearForm = () => {
    setWorkerDetails({
      name: undefined,
      primaryPhone: undefined,
      designation: undefined,
      salary: undefined,
      overtimeHourlyRate: undefined,
      oldBalance: undefined,
      aadhaarNumber: undefined,
      isGroupWorker: false,
      welfareAmount: undefined,
    });
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    setWorkerDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "salary" || name === "overtimeHourlyRate" || name === "oldBalance" || name === "welfareAmount") {
      const num = parseFloat(value);
      const integerRegex = /^[1-9]\d*$/;
      const decimalRegex = /^(?:\d+|\d+\.\d{1,2})$/;

      if (value !== "") {
        if (name === "salary") {
          if (!integerRegex.test(value)) {
            setErrors((prev) => ({
              ...prev,
              [name]: "must be a positive integer",
            }));
          } else {
            setErrors((prev) => {
              const rest = { ...prev };
              delete rest[name];
              return rest;
            });
          }
        } else {
          if (isNaN(num) || num <= 0) {
            setErrors((prev) => ({
              ...prev,
              [name]: "must be greater than 0",
            }));
          } else if (!decimalRegex.test(value)) {
            setErrors((prev) => ({
              ...prev,
              [name]: "only 2 decimal points allowed",
            }));
          } else {
            setErrors((prev) => {
              const rest = { ...prev };
              delete rest[name];
              return rest;
            });
          }
        }
      } else {
        setErrors((prev) => {
          const rest = { ...prev };
          delete rest[name];
          return rest;
        });
      }
    }
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const formatWorkerPayload = (details, isEdit) => {
    const workerData = { ...details };

    // trim and remove empty strings
    Object.keys(workerData).forEach((key) => {
      if (typeof workerData[key] === "string") {
        workerData[key] = workerData[key].trim();
        if (workerData[key] === "") {
          if (isEdit) workerData[key] = null;
          else delete workerData[key];
        }
      }
    });

    return workerData;
  };

  const handleSaveWorker = async () => {
    setErrors({});

    const formattedWorkerDetails = formatWorkerPayload(workerDetails, !!workerId);

    const payload = {
      ...formattedWorkerDetails,
      ...(formattedWorkerDetails.salary != null ? { salary: Number(formattedWorkerDetails.salary) } : {}),
      ...(formattedWorkerDetails.overtimeHourlyRate != null
        ? { overtimeHourlyRate: Number(formattedWorkerDetails.overtimeHourlyRate) }
        : {}),
      ...(formattedWorkerDetails.oldBalance != null ? { oldBalance: Number(formattedWorkerDetails.oldBalance) } : {}),
      ...(formattedWorkerDetails.welfareAmount != null ? { welfareAmount: Number(formattedWorkerDetails.welfareAmount) } : {}),
    };

    if (workerId) delete payload.oldBalance;

    const errors = workerValidationRules(payload);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setLoading(true);
    try {
      if (workerId) {
        await HumanResourceService.updateEmployee(workerId, payload);
        toast.success("Worker updated successfully");
      } else {
        await HumanResourceService.createEmployee({
          ...payload,
          type: "WORKER",
        });
        toast.success("Worker added successfully");
      }

      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${workerId ? "updating" : "adding"} worker`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.log("🚀 ~ handleSaveWorker ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={workerId ? "Edit Worker" : "Add Worker"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveWorker} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Name"
            name="name"
            value={workerDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <PhoneInput
            label="Mobile Number"
            defaultCountry="in"
            value={workerDetails.primaryPhone}
            size="small"
            fullWidth
            onChange={(e164) => setWorkerDetails((prev) => ({ ...prev, primaryPhone: e164 }))}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? "Enter a valid phone number" : " "}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Salary"
            name="salary"
            type="number"
            value={workerDetails.salary}
            onChange={updateDetailsState}
            error={!!errors.salary}
            helperText={errors.salary}
            fullWidth
          />
          <InputField
            label="Overtime Rate"
            name="overtimeHourlyRate"
            type="number"
            value={workerDetails.overtimeHourlyRate}
            onChange={updateDetailsState}
            error={!!errors.overtimeHourlyRate}
            helperText={errors.overtimeHourlyRate}
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Designation"
            name="designation"
            value={workerDetails.designation}
            onChange={updateDetailsState}
            error={!!errors.designation}
            helperText={errors.designation}
            fullWidth
            autoComplete="on"
          />
          {!workerId && (
            <InputField
              label="Old Balance"
              name="oldBalance"
              type="number"
              value={workerDetails.oldBalance}
              onChange={updateDetailsState}
              error={!!errors.oldBalance}
              helperText={errors.oldBalance}
              fullWidth
            />
          )}
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Aadhaar Number"
            name="aadhaarNumber"
            value={workerDetails.aadhaarNumber}
            onChange={updateDetailsState}
            error={!!errors.aadhaarNumber}
            helperText={errors.aadhaarNumber}
            fullWidth
          />
          {hasWeeklyWorkerPaymentModule && (
            <InputField
              label="Welfare Amount"
              name="welfareAmount"
              type="number"
              value={workerDetails.welfareAmount}
              onChange={updateDetailsState}
              error={!!errors.welfareAmount}
              helperText={errors.welfareAmount}
              fullWidth
            />
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mb: 1,
            px: 1.5,
            py: 1,
            borderRadius: "8px",
            border: "1px solid",
            borderColor: workerDetails.isGroupWorker ? "rgba(103, 75, 245, 0.4)" : "#e0e0e0",
            backgroundColor: workerDetails.isGroupWorker ? "rgba(103, 75, 245, 0.06)" : "transparent",
            transition: "all 0.2s ease",
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: workerDetails.isGroupWorker ? "#674BF5" : "text.primary" }}>
              Group Worker
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enable if this worker manages a group with item-based billing
            </Typography>
          </Box>
          <Switch
            checked={Boolean(workerDetails.isGroupWorker)}
            onChange={(e) => setWorkerDetails((prev) => ({ ...prev, isGroupWorker: e.target.checked }))}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#674BF5" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#674BF5" },
            }}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
